import { admittedReportReferenceIndices, projectReportSourceEvidence } from '../../../../lib/reportGeneration/reportEvidenceSourceAdmission';
import React from 'react';
import type { ReportTheme } from '../../../../types/reportTheme';
import type { SiteRagReport } from '../../../../types/siteRag';
import { roleColor } from '../../../../lib/reportRoleColors';
import { REPORT_LINE_HEIGHT, reportShellBodyFontSize, REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';
import { resolveEditorialLayer } from '../../../../lib/editorial/resolveEditorialLayer';
import { stripAppOnboardingCopy } from '../../../../lib/safeNarrativeProse';
import { walkClaimsText } from '../../../../lib/walkClaimsText';
import { computeEffectiveAppendixVisibleRefIndices } from '../../../../lib/reportRendering';

/**
 * C0-GEN Wave 3 — the flowing editorial PROSE BODY.
 *
 * Renders a section's GENERATED `editorialNarrative` (a grounding-validated
 * re-voicing of the already-verified section content) as the magazine article:
 * paragraph-split flowing prose, the first paragraph carrying the drop-cap hook
 * (`data-editorial-lead`), and the generated `pullQuote` (a verbatim lift +
 * deterministic attribution) emitted as a feature pull-quote after the lead.
 * Both the prose `[data-editorial-lead]::first-letter` drop-cap and the
 * `[data-editorial-pullquote]` styling are scoped CSS under
 * `[data-report-root][data-editorial]` (src/index.css), so this component is
 * pure layout — it never invents a style hook the classic render lacks.
 *
 * CONTRACT: the caller mounts this ONLY when `hasGeneratedEditorialLayer(report,
 * sectionId)` is true. When no generated layer exists the resolver would return
 * the deterministic fallback (today's verified prose), which the section's
 * EXISTING structured blocks already render — so mounting this on the fallback
 * path would DOUBLE the prose. Mounting it only on the layer-present path keeps
 * the flag-OFF / no-layer DOM byte-identical (the `reportSurfaceParity` oracle).
 *
 * `data-flow="flow"` so the prose packs into the page columns under the N2
 * 2-col page-flow engine (the prose register, not an exhibit).
 *
 * Pure + total: every number is already cited in the verified content; `[Ref N]`
 * pills route through the SAME `walkClaimsText` the rest of the report uses, so
 * citations stay resolvable and atomic.
 */
export interface EditorialProseBodyProps {
  report: SiteRagReport;
  sectionId: string;
  theme: ReportTheme;
}

/** Split a narrative into paragraphs on blank lines (then single newlines). */
function splitParagraphs(prose: string): string[] {
  const byBlank = prose
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  if (byBlank.length > 1) return byBlank;
  // No blank-line breaks — fall back to single-newline paragraphs.
  return prose
    .split(/\n+/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
}

/**
 * Redhead review wave 3 (defect 3) — normalize prose for the pull-quote
 * ECHO check: strip `[Ref N]` / reliability tokens, lowercase, collapse
 * every non-alphanumeric run to a single space. Mirrors the Conclusion
 * WHY-cap de-dup posture (v1.17.45): a quote whose normalized text is a
 * substring of the normalized narrative is a STRUCTURAL ECHO (the narrative
 * already carries the sentence verbatim), so featuring it again as a
 * standalone block reads as an accidental duplicated paragraph — suppress it.
 * A genuinely re-voiced narrative (the normal case: the pull-quote is a
 * verbatim lift from the VERIFIED content, the narrative a re-voicing)
 * normalizes differently, so real pull-quotes keep rendering.
 */
function normalizeForEchoMatch(s: string): string {
  return s
    .replace(/\[(?:(?:verified|inferred|unverified)\s+)?Ref\s+\d+\]/gi, ' ')
    .replace(/\[(?:verified|inferred|unverified)(?:\s+[^\]]*)?\]/gi, ' ')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export const EditorialProseBody: React.FC<EditorialProseBodyProps> = ({
  report,
  sectionId,
  theme,
}) => {
  report = projectReportSourceEvidence(report);
  const layer = resolveEditorialLayer(report, sectionId);
  // Redhead wave 3 (defect 7) — app onboarding copy ("Set Project Intent…")
  // must never render inside report body prose, even when a legacy generated
  // narrative re-voiced it verbatim.
  const narrative = stripAppOnboardingCopy(layer.editorialNarrative?.trim() ?? '');
  if (!narrative) return null;

  const appendixVisibleRefIndices = admittedReportReferenceIndices(report, computeEffectiveAppendixVisibleRefIndices(
    report.references,
    { reportType: report.reportType },
  ) ?? undefined);
  const renderCited = (text: string): React.ReactNode[] =>
    walkClaimsText(text, report.references, report.claims, appendixVisibleRefIndices);

  const paragraphs = splitParagraphs(narrative);
  const pullQuote = layer.pullQuote;
  // AI LAYOUT/DESIGN PASS (emphasis.pullQuote) — when a layout plan drove this
  // report, the plan OWNS the per-section pull-quote decision: the (always
  // grounded) verbatim pull-quote is FEATURED only where the plan set
  // `emphasis.pullQuote === true`. ABSENT plan ⇒ today's exact behaviour
  // (feature whenever the editorial layer carries a grounded quote), so the
  // no-plan render is byte-identical. The plan NEVER authors the quote text — it
  // only decides whether to surface the verbatim lift the editorial layer
  // already produced, so this is a pure DESIGN decision: the same sentence
  // remains in the flowing prose either way (no fact / citation change).
  const planPullQuote =
    report.layoutPlan?.sections?.[sectionId]?.emphasis?.pullQuote;
  const planAllowsPullQuote = report.layoutPlan ? planPullQuote === true : true;
  // Redhead review wave 3 (defect 3) — suppress the pull-quote when it is a
  // NEAR-VERBATIM ECHO of the narrative itself (the re-voiced prose kept the
  // lifted sentence verbatim, so the "feature quote" reads as the same
  // sentence twice in immediate succession, plus its attribution stamp
  // rendering as stray body prose). The suppression is display-only — the
  // sentence remains in the flowing prose; nothing is lost.
  // The Studio/sample PREVIEW layer (buildPreviewEditorialNarrative) lifts
  // the narrative's own first sentence BY DESIGN so template authors see the
  // feature pull-quote treatment — exempt that deterministic demo layer from
  // the echo suppression (its marker is `modelUsed: 'preview-deterministic'`).
  const isPreviewLayer = report.editorialNarrative?.modelUsed === 'preview-deterministic';
  const quoteText = typeof pullQuote?.text === 'string' ? pullQuote.text.trim() : '';
  const quoteEchoesNarrative = (() => {
    if (!quoteText || isPreviewLayer) return false;
    const quoteNorm = normalizeForEchoMatch(quoteText);
    if (!quoteNorm) return false;
    const echoed = normalizeForEchoMatch(narrative).includes(quoteNorm);
    if (echoed) {
      console.warn(
        `[EditorialProseBody] suppressed pull-quote for sectionId=${sectionId} — quote text is a near-verbatim echo of the section narrative`,
      );
    }
    return echoed;
  })();
  const hasPullQuote =
    planAllowsPullQuote
    && quoteText.length > 0
    && !quoteEchoesNarrative;

  return (
    <div
      data-editorial-prose-body="true"
      data-flow="flow"
      // Body size flows from the ONE unified scale (reportShellBodyFontSize ==
      // reportTypographySizes(theme).body) so the magazine-prose tracks the
      // densified body baseline instead of a parallel hardcoded 14px.
      style={{ color: roleColor(theme, 'body'), fontSize: reportShellBodyFontSize(theme), lineHeight: REPORT_LINE_HEIGHT.body }}
    >
      {paragraphs.map((para, i) => (
        <React.Fragment key={i}>
          <p
            // The LEAD paragraph carries the drop-cap hook (raised serif initial,
            // scoped CSS). Subsequent paragraphs flow normally.
            {...(i === 0 ? { 'data-editorial-lead': 'true' } : {})}
            style={{ margin: '0 0 12px' }}
          >
            {renderCited(para)}
          </p>
          {/* Pull-quote emitted after the lead paragraph — a feature break in
              the article. Verbatim lift + deterministic attribution. */}
          {i === 0 && hasPullQuote && (
            <blockquote
              data-editorial-pullquote
              data-flow="flow"
              data-testid={`editorial-pullquote-${sectionId}`}
            >
              {renderCited(pullQuote!.text)}
              {typeof pullQuote!.attribution === 'string'
                && pullQuote!.attribution.trim().length > 0 && (
                <cite
                  data-editorial-pullquote-attr="true"
                  style={{
                    display: 'block',
                    marginTop: 8,
                    // House-style 2026-06-29 — attribution in the Hanken apparatus
                    // face (matches the centered pull-quote's editorial register).
                    fontFamily: theme.apparatusFont ?? theme.bodyFont,
                    fontSize: REPORT_TEXT_PX.caption,
                    fontStyle: 'normal',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: roleColor(theme, 'muted'),
                  }}
                >
                  {pullQuote!.attribution}
                </cite>
              )}
            </blockquote>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};
